import { createServerFn } from "@tanstack/react-start";
import { getRequest } from "@tanstack/react-start/server";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";

let _admin: any = null;
function getAdmin() {
  if (!_admin) {
    _admin = createClient(process.env.SUPABASE_URL!, process.env.SUPABASE_SERVICE_ROLE_KEY!);
  }
  return _admin;
}

async function assertAdmin(userId: string) {
  const admin = getAdmin();
  const { data, error } = await admin
    .from("app_admins")
    .select("user_id")
    .eq("user_id", userId)
    .maybeSingle();
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden");
}

function makeCode(): string {
  // 12-char human-friendly code (no ambiguous chars)
  const alphabet = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let out = "";
  for (let i = 0; i < 12; i++) {
    out += alphabet[Math.floor(Math.random() * alphabet.length)];
    if (i === 3 || i === 7) out += "-";
  }
  return out;
}

/* ---------------- Admin: create grant ---------------- */
const CreateSchema = z.object({
  tier: z.enum(["premium", "pro"]),
  beneficiary_email: z.string().email().optional(),
  note: z.string().max(500).optional(),
});

export const createAccessGrant = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => CreateSchema.parse(d))
  .handler(async ({ data, context }) => {
    const { userId } = context;
    await assertAdmin(userId);
    const admin = getAdmin();
    const code = makeCode();
    const { data: row, error } = await admin
      .from("access_grants")
      .insert({
        code,
        tier: data.tier,
        beneficiary_email: data.beneficiary_email ?? null,
        note: data.note ?? null,
        created_by: userId,
      })
      .select("id, code, tier, beneficiary_email, note, active, created_at")
      .single();
    if (error) throw new Error(error.message);

    // Fire-and-forget: email the code to the beneficiary if an address was provided.
    let emailed: "sent" | "skipped" | "failed" = "skipped";
    if (data.beneficiary_email) {
      try {
        const req = getRequest();
        const origin = new URL(req!.url).origin;
        const authHeader = req!.headers.get("authorization") ?? "";
        const res = await fetch(`${origin}/lovable/email/transactional/send`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: authHeader,
          },
          body: JSON.stringify({
            templateName: "access-code",
            recipientEmail: data.beneficiary_email,
            idempotencyKey: `access-code-${row.id}`,
            templateData: {
              code: row.code,
              tier: data.tier,
              redeemUrl: `${origin}/billing`,
              note: data.note,
            },
          }),
        });
        emailed = res.ok ? "sent" : "failed";
        if (!res.ok) {
          console.error("[access-grant] email send failed", res.status, await res.text());
        }
      } catch (e) {
        console.error("[access-grant] email send error", e);
        emailed = "failed";
      }
    }
    return { ...row, emailed };
  });

/* ---------------- Admin: list grants ---------------- */
export const listAccessGrants = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const { userId } = context;
    await assertAdmin(userId);
    const admin = getAdmin();
    const { data, error } = await admin
      .from("access_grants")
      .select("id, code, tier, beneficiary_email, beneficiary_user_id, note, active, revoked_at, redeemed_at, last_used_at, created_at")
      .order("created_at", { ascending: false })
      .limit(200);
    if (error) throw new Error(error.message);
    return { items: data ?? [] };
  });

/* ---------------- Admin: revoke grant ---------------- */
const RevokeSchema = z.object({ id: z.string().uuid() });
export const revokeAccessGrant = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => RevokeSchema.parse(d))
  .handler(async ({ data, context }) => {
    const { userId } = context;
    await assertAdmin(userId);
    const admin = getAdmin();
    const { error } = await admin
      .from("access_grants")
      .update({ active: false, revoked_at: new Date().toISOString() })
      .eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true as const };
  });

/* ---------------- Authenticated user: check if admin ---------------- */
export const checkIsAdmin = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }) => {
    const admin = getAdmin();
    const { data } = await admin
      .from("app_admins")
      .select("user_id")
      .eq("user_id", context.userId)
      .maybeSingle();
    return { isAdmin: !!data };
  });

/* ---------------- Authenticated user: redeem code ---------------- */
const RedeemSchema = z.object({
  code: z.string().trim().min(4).max(40),
});
export const redeemAccessGrant = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d) => RedeemSchema.parse(d))
  .handler(async ({ data, context }) => {
    const { userId } = context;
    const admin = getAdmin();
    const normalized = data.code.toUpperCase().replace(/\s+/g, "");
    const { data: row, error } = await admin
      .from("access_grants")
      .select("id, tier, active, revoked_at, beneficiary_user_id, beneficiary_email")
      .eq("code", normalized)
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) throw new Error("Code introuvable");
    if (!row.active || row.revoked_at) throw new Error("Code révoqué");
    if (row.beneficiary_user_id && row.beneficiary_user_id !== userId) {
      throw new Error("Ce code a déjà été utilisé");
    }
    const { error: upErr } = await admin
      .from("access_grants")
      .update({
        beneficiary_user_id: userId,
        redeemed_at: row.beneficiary_user_id ? undefined : new Date().toISOString(),
        last_used_at: new Date().toISOString(),
      })
      .eq("id", row.id);
    if (upErr) throw new Error(upErr.message);
    return { ok: true as const, tier: row.tier as "premium" | "pro" };
  });